import React, { useState, useEffect } from 'react';
import { Instagram, Youtube, Twitter, ShoppingBag, BookOpen, Calendar, Gift, Link as LinkIcon, Loader2, X } from 'lucide-react';
import { getStorefrontSettings } from '../firebase/storefront';
import { getAllProducts } from '../firebase/products';
import { Checkout } from '../components/Checkout';
import { BookingCalendar } from '../components/BookingCalendar';
import { formatDisplayDate } from '../utils/dateHelpers';

export function Storefront() {
  const [settings, setSettings] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [showCheckout, setShowCheckout] = useState(false);
  // Booking modal state
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedBookingProduct, setSelectedBookingProduct] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [settingsData, productsData] = await Promise.all([
        getStorefrontSettings(),
        getAllProducts()
      ]);
      setSettings(settingsData);
      setProducts(productsData || []);
    } catch (error) {
      console.error('Error loading storefront:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check if product requires booking (type: 'coaching_session' or has requiresBooking flag)
  const isBookingProduct = (product) => {
    return product.type === 'coaching_session' || product.type === 'booking' || product.requiresBooking;
  };

  const addToCart = (product) => {
    // If it's a booking product, open the booking modal instead
    if (isBookingProduct(product)) {
      setSelectedBookingProduct(product);
      setShowBookingModal(true);
      return;
    }

    // Regular product - add directly to cart
    addProductToCart(product);
  };

  // Helper to add product to cart (used by both regular and booking flows)
  const addProductToCart = (product, bookingInfo = null) => {
    // For booking products with date/time, create a unique cart key
    const cartKey = bookingInfo
      ? `${product.id}-${bookingInfo.date.toISOString()}-${bookingInfo.time}`
      : product.id;

    const existingItem = cart.find(item => item.cartKey === cartKey);
    if (existingItem && !bookingInfo) {
      // Only allow quantity increase for non-booking items
      setCart(cart.map(item =>
        item.cartKey === cartKey
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, {
        cartKey,
        productId: product.id,
        title: product.title,
        price: product.price,
        thumbnail: product.thumbnail,
        quantity: 1,
        // Booking-specific fields
        ...(bookingInfo && {
          bookingDate: bookingInfo.date,
          bookingTime: bookingInfo.time,
          bookingDisplayDate: formatDisplayDate(bookingInfo.date)
        })
      }]);
    }
  };

  // Handler when a booking slot is selected in the modal
  const handleBookingSlotSelected = (date, time) => {
    if (selectedBookingProduct) {
      addProductToCart(selectedBookingProduct, { date, time });
      setShowBookingModal(false);
      setSelectedBookingProduct(null);
    }
  };

  const removeFromCart = (cartKey) => {
    setCart(cart.filter(item => item.cartKey !== cartKey));
  };

  const updateCartQuantity = (cartKey, quantity) => {
    if (quantity <= 0) {
      removeFromCart(cartKey);
      return;
    }
    setCart(cart.map(item =>
      item.cartKey === cartKey
        ? { ...item, quantity }
        : item
    ));
  };

  const getSectionProducts = (section) => {
    if (!section.productIds || section.productIds.length === 0) return [];
    return products.filter(p => section.productIds.includes(p.id));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fdfbf7] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#c5a059] animate-spin" />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="min-h-screen bg-[#fdfbf7] flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-gray-600">Storefront not configured yet.</p>
        </div>
      </div>
    );
  }

  const colors = settings.colors || {
    primary: '#c5a059',
    secondary: '#d48c95',
    background: '#fdfbf7',
    text: '#1f2937'
  };

  if (showCheckout) {
    return (
      <Checkout
        items={cart}
        onSuccess={() => {
          setCart([]);
          setShowCheckout(false);
        }}
        onCancel={() => setShowCheckout(false)}
      />
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-xl font-serif" style={{ color: colors.primary }}>
            {settings.displayName || 'Store'}
          </h1>
          <div className="flex items-center gap-4">
            {cart.length > 0 && (
              <button
                onClick={() => setShowCheckout(true)}
                className="relative flex items-center gap-2 px-4 py-2 rounded-lg text-white transition"
                style={{ backgroundColor: colors.primary }}
              >
                <ShoppingBag size={20} />
                <span>Cart ({cart.length})</span>
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {cart.reduce((sum, item) => sum + item.quantity, 0)}
                </span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Profile Section */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="text-center mb-16 sm:mb-20">
          {settings.profilePicture && (
            <div className="relative inline-block mb-6">
              <div
                className="absolute inset-0 rounded-full blur-2xl opacity-30"
                style={{ backgroundColor: colors.primary }}
              ></div>
              <img
                src={settings.profilePicture}
                alt={settings.displayName || 'Profile'}
                className="relative w-32 h-32 sm:w-40 sm:h-40 rounded-full mx-auto object-cover border-4 border-white shadow-2xl transform hover:scale-105 transition-transform duration-300"
                style={{ borderColor: colors.primary }}
              />
            </div>
          )}
          <h1
            className="text-4xl sm:text-5xl font-serif font-bold mb-4"
            style={{ color: colors.primary }}
          >
            {settings.displayName || 'Store'}
          </h1>
          {settings.bio && (
            <p className="text-lg sm:text-xl text-gray-700 max-w-3xl mx-auto mb-8 leading-relaxed">
              {settings.bio}
            </p>
          )}

          {/* Social Links */}
          {(settings.socialLinks?.instagram || settings.socialLinks?.tiktok ||
            settings.socialLinks?.youtube || settings.socialLinks?.twitter) && (
              <div className="flex items-center justify-center gap-3 sm:gap-4">
                {settings.socialLinks.instagram && (
                  <a
                    href={settings.socialLinks.instagram.startsWith('http')
                      ? settings.socialLinks.instagram
                      : `https://instagram.com/${settings.socialLinks.instagram.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 rounded-full hover:bg-gradient-to-br hover:from-pink-500 hover:to-purple-500 transition-all duration-300 transform hover:scale-110 hover:shadow-lg"
                    style={{ backgroundColor: colors.primary + '15' }}
                  >
                    <Instagram size={24} style={{ color: colors.primary }} />
                  </a>
                )}
                {settings.socialLinks.tiktok && (
                  <a
                    href={settings.socialLinks.tiktok.startsWith('http')
                      ? settings.socialLinks.tiktok
                      : `https://tiktok.com/@${settings.socialLinks.tiktok.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 rounded-full hover:bg-gradient-to-br hover:from-black hover:to-gray-800 transition-all duration-300 transform hover:scale-110 hover:shadow-lg"
                    style={{ backgroundColor: colors.primary + '15' }}
                  >
                    <span className="font-bold text-lg" style={{ color: colors.primary }}>TK</span>
                  </a>
                )}
                {settings.socialLinks.youtube && (
                  <a
                    href={settings.socialLinks.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 rounded-full hover:bg-gradient-to-br hover:from-red-500 hover:to-red-600 transition-all duration-300 transform hover:scale-110 hover:shadow-lg"
                    style={{ backgroundColor: colors.primary + '15' }}
                  >
                    <Youtube size={24} style={{ color: colors.primary }} />
                  </a>
                )}
                {settings.socialLinks.twitter && (
                  <a
                    href={settings.socialLinks.twitter.startsWith('http')
                      ? settings.socialLinks.twitter
                      : `https://twitter.com/${settings.socialLinks.twitter.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 rounded-full hover:bg-gradient-to-br hover:from-blue-400 hover:to-blue-500 transition-all duration-300 transform hover:scale-110 hover:shadow-lg"
                    style={{ backgroundColor: colors.primary + '15' }}
                  >
                    <Twitter size={24} style={{ color: colors.primary }} />
                  </a>
                )}
              </div>
            )}
        </div>

        {/* Sections */}
        {settings.sections && settings.sections.length > 0 && (
          <div className="space-y-16 sm:space-y-20">
            {settings.sections.map((section, sectionIdx) => {
              const sectionProducts = getSectionProducts(section);

              if (sectionProducts.length === 0) return null;

              const getSectionIcon = (type) => {
                switch (type) {
                  case 'products': return <ShoppingBag size={24} />;
                  case 'courses': return <BookOpen size={24} />;
                  case 'memberships': return <Users size={24} />;
                  case 'freebies': return <Gift size={24} />;
                  case 'bookings': return <Calendar size={24} />;
                  case 'custom': return <LinkIcon size={24} />;
                  default: return <ShoppingBag size={24} />;
                }
              };

              const getSectionGradient = (type) => {
                switch (type) {
                  case 'products': return 'from-blue-500/10 via-blue-400/5 to-transparent';
                  case 'courses': return 'from-purple-500/10 via-purple-400/5 to-transparent';
                  case 'memberships': return 'from-pink-500/10 via-pink-400/5 to-transparent';
                  case 'freebies': return 'from-green-500/10 via-green-400/5 to-transparent';
                  case 'bookings': return 'from-orange-500/10 via-orange-400/5 to-transparent';
                  default: return 'from-[#c5a059]/10 via-[#d48c95]/5 to-transparent';
                }
              };

              return (
                <div
                  key={section.id}
                  className="relative"
                  style={{ animationDelay: `${sectionIdx * 100}ms` }}
                >
                  {/* Section Header */}
                  <div className="mb-8 sm:mb-10">
                    <div className="flex items-center gap-4 mb-3">
                      <div
                        className="p-3 rounded-2xl shadow-lg flex items-center justify-center"
                        style={{
                          background: `linear-gradient(135deg, ${colors.primary}15, ${colors.secondary}15)`,
                          border: `2px solid ${colors.primary}30`
                        }}
                      >
                        <div style={{ color: colors.primary }}>
                          {getSectionIcon(section.type)}
                        </div>
                      </div>
                      <div>
                        <h2
                          className="text-3xl sm:text-4xl font-serif font-bold mb-2"
                          style={{ color: colors.primary }}
                        >
                          {section.title || 'Products'}
                        </h2>
                        <div className="h-1 w-20 rounded-full" style={{ backgroundColor: colors.primary }}></div>
                      </div>
                    </div>
                  </div>

                  {/* Products Grid */}
                  <div className={`relative bg-gradient-to-br ${getSectionGradient(section.type)} rounded-3xl p-6 sm:p-8 -mx-4 sm:mx-0`}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                      {sectionProducts.map((product, productIdx) => (
                        <div
                          key={product.id}
                          className="group bg-white rounded-2xl shadow-lg border-2 border-gray-100 overflow-hidden hover:shadow-2xl hover:border-[#c5a059] transition-all duration-300 transform hover:-translate-y-2"
                          style={{ animationDelay: `${productIdx * 50}ms` }}
                        >
                          {/* Product Image */}
                          <div className="relative overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
                            {product.thumbnail ? (
                              <img
                                src={product.thumbnail}
                                alt={product.title}
                                className="w-full h-56 sm:h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                              />
                            ) : (
                              <div
                                className="w-full h-56 sm:h-64 flex items-center justify-center"
                                style={{
                                  background: `linear-gradient(135deg, ${colors.primary}20, ${colors.secondary}20)`
                                }}
                              >
                                <ShoppingBag size={48} style={{ color: colors.primary }} className="opacity-50" />
                              </div>
                            )}
                            {/* Price Badge Overlay */}
                            <div className="absolute top-4 right-4">
                              <div
                                className="px-4 py-2 rounded-full shadow-lg backdrop-blur-sm font-bold text-white"
                                style={{ backgroundColor: colors.primary }}
                              >
                                {(() => {
                                  const price = parseFloat(product.price) || 0;
                                  return isNaN(price) || price === 0 ? 'Free' : `£${price.toFixed(2)}`;
                                })()}
                              </div>
                            </div>
                            {/* Hover Gradient Overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          </div>

                          {/* Product Content */}
                          <div className="p-6">
                            <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-[#c5a059] transition-colors">
                              {product.title}
                            </h3>
                            {product.description && (
                              <p className="text-sm text-gray-600 mb-4 line-clamp-2 min-h-[2.5rem]">
                                {product.description}
                              </p>
                            )}
                            <button
                              onClick={() => addToCart(product)}
                              className="w-full py-3 rounded-xl font-semibold text-white transition-all duration-200 shadow-md hover:shadow-xl transform hover:scale-105 active:scale-95"
                              style={{
                                background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`
                              }}
                            >
                              Add to Cart
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {(!settings.sections || settings.sections.length === 0) && (
          <div className="text-center py-16 sm:py-20">
            <div
              className="inline-flex p-6 rounded-3xl mb-6 shadow-lg"
              style={{
                background: `linear-gradient(135deg, ${colors.primary}15, ${colors.secondary}15)`,
                border: `2px solid ${colors.primary}30`
              }}
            >
              <ShoppingBag size={48} style={{ color: colors.primary }} />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">No Sections Yet</h3>
            <p className="text-gray-500 max-w-md mx-auto">
              Your storefront sections will appear here once you've created them in the admin panel.
            </p>
          </div>
        )}
      </div>

      {/* Booking Calendar Modal */}
      {showBookingModal && selectedBookingProduct && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => {
              setShowBookingModal(false);
              setSelectedBookingProduct(null);
            }}
          />

          {/* Modal Content */}
          <div className="relative z-10 w-full max-w-lg">
            {/* Close button */}
            <button
              onClick={() => {
                setShowBookingModal(false);
                setSelectedBookingProduct(null);
              }}
              className="absolute -top-3 -right-3 z-20 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition"
              aria-label="Close booking calendar"
            >
              <X size={20} />
            </button>

            {/* Product info header */}
            <div className="bg-white rounded-t-xl px-4 py-3 border-b border-gray-100">
              <p className="text-sm text-gray-500">Booking for:</p>
              <h3 className="font-semibold text-gray-800">{selectedBookingProduct.title}</h3>
              <p className="text-[#c5a059] font-bold">
                {parseFloat(selectedBookingProduct.price) > 0
                  ? `£${parseFloat(selectedBookingProduct.price).toFixed(2)}`
                  : 'Free'
                }
              </p>
            </div>

            {/* Calendar component */}
            <BookingCalendar
              onSlotSelect={handleBookingSlotSelected}
              onCancel={() => {
                setShowBookingModal(false);
                setSelectedBookingProduct(null);
              }}
              title="Select Session Time"
              subtitle="Choose your preferred date and time"
            />
          </div>
        </div>
      )}
    </div>
  );
}



